import React, { useEffect, useState } from 'react';
import Text from 'components/_UI/Text';
import styled, { keyframes } from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import { FullyCentered } from 'styled/alignment/Center';
import eventsApi from 'api/requests/events';
import { useEventsNearToYou } from 'context/events/EventsNearToYou';
import Button from 'components/_UI/Button';
import colors from 'theme/colors';
import { useCurrentLocation } from 'context/user/CurrentLocation';
import { useEventFilter } from 'context/events/EventFilter';
import { useUserLocation } from 'context/user/UserLocation';
import EventCardSkeleton from 'components/commons/skeletons/EventCardSkeleton';
import EventCardV2 from '../card/EventCardV2';

const PER_PAGE = 9;

export default function EventsNearToYouV2({
    wrap = false,
    onEventClick,
    searchEvents = () => {},
}) {
    const { eventsNearToYou, setEventsNearToYou } = useEventsNearToYou();
    const { currentLocation } = useCurrentLocation();
    const { eventFilter } = useEventFilter();
    const { userLocation } = useUserLocation();

    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(null);

    useEffect(() => {
        getEventsNearToYou();
    }, [currentLocation?.city]);

    useEffect(() => {
        if (eventFilter === undefined) return;
        if (eventFilter) {
            getFilteredEvents();
        } else {
            getEventsNearToYou();
        }
    }, [eventFilter]);

    function buildFilterPayload() {
        const payload = { city: currentLocation?.city, state: currentLocation?.state };
        if (eventFilter?.term) payload.term = eventFilter.term;
        if (eventFilter?.dates) payload.filter_date = eventFilter.dates;
        if (eventFilter?.classification_id) payload.classification_id = eventFilter.classification_id;
        if (eventFilter?.categories?.length) payload.tags = eventFilter.categories.join(',');
        if (eventFilter?.distance_range && userLocation?.lat) {
            payload.distance_range = eventFilter.distance_range;
            payload.lat = userLocation.lat;
            payload.long = userLocation.long;
        }
        return payload;
    }

    async function getFilteredEvents() {
        setLoading(true);
        try {
            const { data: res } = await eventsApi.filter(buildFilterPayload());
            setEventsNearToYou(res?.data || []);
            setTotalCount(res?.meta?.total ?? (res?.data?.length || 0));
        } catch (error) {
            console.error('Error fetching filtered events:', error);
        } finally {
            setLoading(false);
        }
    }

    async function getEventsNearToYou() {
        setLoading(true);
        try {
            const payload = { limit: PER_PAGE, page: 1 };
            if (currentLocation?.city) {
                payload.city = currentLocation.city;
                payload.state = currentLocation.state;
            }
            const { data: res } = await eventsApi.filter(payload);
            setEventsNearToYou(res?.data || []);
            setTotalCount(res?.meta?.total ?? (res?.data?.length || 0));
        } catch (error) {
            console.error('Error fetching events near to you:', error);
        } finally {
            setLoading(false);
        }
    }

    const displayedItems = eventsNearToYou;

    return (
        <NearToYouContainer>
            {!eventsNearToYou || loading ? (
                <SkeletonContainer>
                    {[1, 2, 3, 4, 5, 6].map((_, i) => (
                        <EventCardSkeleton key={i} />
                    ))}
                </SkeletonContainer>
            ) : eventsNearToYou?.length === 0 ? (
                <ContainerNotEvents>
                    <ContentNotEvents>
                        <Column verticalCenter>
                            <Text name='default' type='bold' text-align='center'>
                                Não encontramos nenhum evento próximo a você
                            </Text>
                            <ContainerButtonSearch>
                                <Button id={'not-found-events-button'} onClick={() => searchEvents()}>
                                    Procurar eventos
                                </Button>
                            </ContainerButtonSearch>
                        </Column>
                    </ContentNotEvents>
                </ContainerNotEvents>
            ) : (
                <>
                    <Row>
                        <Text>
                            {eventsNearToYou.length === 1 ? 'Foi encontrado' : 'Foram encontrados'}
                        </Text>
                        <Text type='bold' color={colors.nightDark}>
                            &nbsp;{totalCount ?? eventsNearToYou.length}
                        </Text>
                        <Text>&nbsp;eventos em</Text>
                        <Text type='bold' color={colors.nightDark}>
                            &nbsp;{currentLocation?.city || 'sua região'}
                        </Text>
                    </Row>

                    <CardsContainer wrapContent={wrap}>
                        {displayedItems?.map((event, index) => (
                            <AnimatedCard key={event.slug} $index={index}>
                                <EventCardV2
                                    whiteBackground={true}
                                    data={event}
                                    onEventClick={() => onEventClick(event)}
                                />
                            </AnimatedCard>
                        ))}
                    </CardsContainer>
                </>
            )}
        </NearToYouContainer>
    );
}

const NearToYouContainer = styled(Column)`
    margin-top: 16px;
    @media screen and (max-width: ${breakpoints.xl}) {
        display: flex;
    }
`;

const CardsContainer = styled(Row)`
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;

    & > div {
        min-width: 75vw;
        width: 75vw;
        flex-shrink: 0;
        margin-left: 12px;
    }

    & > div:first-child {
        margin-left: 0;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
        overflow-x: hidden;
        padding-bottom: 0;
        align-items: start;

        & > div {
            width: 100%;
            min-width: 0;
            margin: 0;
        }
    }
`;

const ContainerNotEvents = styled(Column)`
    width: 100%;
    @media screen and (min-width: ${breakpoints.xm}) and (max-width: ${breakpoints.lg}) {
        display: flex;
        align-items: center;
        height: 180px;
        justify-content: center;
    }
`;

const ContentNotEvents = styled(FullyCentered)`
    width: 100%;
    text-align: center;
    align-items: center;
    display: flex;
`;

const ContainerButtonSearch = styled(FullyCentered)`
    width: 200px;
`;

const SkeletonContainer = styled(Row)`
    margin-top: 16px;
    flex-wrap: nowrap;
    overflow-x: hidden;

    & > div {
        min-width: 75vw;
        width: 75vw;
        flex-shrink: 0;
        margin-left: 12px;
    }

    & > div:first-child {
        margin-left: 0;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-wrap: wrap;
        overflow-x: unset;
        margin-top: 30px;

        & > div {
            width: 31.5%;
            min-width: unset;
            margin-left: 1.5%;
            margin-top: 20px;
            & > div {
                width: 100%;
            }
        }

        & > div:first-child {
            margin-left: 1.5%;
        }
    }
`;

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(16px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const AnimatedCard = styled.div`
    width: 100%;
    animation: ${fadeInUp} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: ${({ $index }) => Math.min($index * 50, 300)}ms;
`;
